import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Crown, Ticket, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trackEvent } from './GA4';
import { getEventImage, trackEventBackend } from '@/lib/eventHelpers';

function formatDate(dateStr, locale = 'it-IT') {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return dateStr; }
}

export default function EventCard({ evento, locale, large = false, onTrack }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en-GB' : 'it-IT';
  const titolo = lang === 'en-GB' && evento.titolo_en ? evento.titolo_en : evento.titolo;
  const showBuy = !!(evento.link_acquisto && evento.link_acquisto.trim());
  const showTable = !!evento.tavolo_prive && !!(evento.link_tavolo && evento.link_tavolo.trim());

  const handleBuy = (e) => {
    e.stopPropagation();
    trackEvent('click_acquista', { event_id: evento.id, slug: evento.slug });
    trackEventBackend(evento.slug, 'buy');
    onTrack?.('buy');
    window.open(evento.link_acquisto, '_blank', 'noopener');
  };
  const handleTable = (e) => {
    e.stopPropagation();
    trackEvent('click_tavolo', { event_id: evento.id, slug: evento.slug });
    trackEventBackend(evento.slug, 'table');
    onTrack?.('table');
    window.open(evento.link_tavolo, '_blank', 'noopener');
  };

  const imageSrc = getEventImage(evento, locale);

  return (
    <article data-testid="event-card" className={`group rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden hover:border-[rgba(214,185,140,0.4)] hover:shadow-[0_18px_60px_rgba(0,0,0,0.6)] transition-[border-color,box-shadow] duration-200 flex flex-col`}>
      <Link to={`/eventi/${evento.slug}`} className="block relative">
        <div className={`relative ${large ? 'aspect-[16/8]' : 'aspect-[16/9]'} bg-[color:var(--surface-2)] overflow-hidden`}>
          {imageSrc ? (
            <img src={imageSrc} alt={titolo} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-[color:var(--gold)] opacity-40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
            {evento.in_evidenza && (<Badge className="bg-[color:var(--gold-strong)] text-black hover:bg-[color:var(--gold-strong)] font-semibold" data-testid="event-card-featured-badge">{t('event.featuredBadge')}</Badge>)}
            {evento.tavolo_prive && (<Badge variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)]"><Crown className="w-3 h-3 mr-1"/>{t('event.priveBadge')}</Badge>)}
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <h3 data-testid="event-card-title" className={`font-heading text-[color:var(--text)] ${large ? 'text-2xl md:text-3xl' : 'text-xl'} leading-tight drop-shadow-lg`}>{titolo}</h3>
          </div>
        </div>
      </Link>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-3 text-xs text-[color:var(--text-muted)] flex-wrap">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[color:var(--gold)]" /> {formatDate(evento.data, lang)} • {evento.orario}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[color:var(--gold)]" /> {locale?.nome || ''}{locale?.nome && evento.zona ? ' • ' : ''}{evento.zona}</span>
        </div>
        {evento.descrizione && (
          <p className="text-sm text-[color:var(--text-muted)] line-clamp-2">{(evento.descrizione || '').replace(/<[^>]+>/g,'').slice(0, 140)}</p>
        )}
        <div className="flex items-center gap-2 mt-auto pt-2">
          {showBuy && (
            <Button onClick={handleBuy} data-testid="event-card-buy-button" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold btn-press flex-1">
              <Ticket className="w-4 h-4 mr-1.5" /> {t('event.buy')}
            </Button>
          )}
          {showTable && (
            <Button onClick={handleTable} data-testid="event-card-table-button" variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)] hover:text-[color:var(--gold-soft)] btn-press flex-1">
              <Crown className="w-4 h-4 mr-1.5" /> {t('event.table')}
            </Button>
          )}
          {!showBuy && !showTable && (
            <Link to={`/eventi/${evento.slug}`} className="text-sm text-[color:var(--gold)] hover:text-[color:var(--gold-soft)] flex-1">{t('event.viewMore')} →</Link>
          )}
        </div>
      </div>
    </article>
  );
}
